import React, { useState, useEffect, useCallback } from 'react';
import { advancedReportService, categoryService, branchService } from '../../services/api';
import { Card, Table, PageHeader, useToast } from '../../components/common';
import { usePageTitle } from '../../context/PageTitleContext';
import { useSidebar } from '../../context/SidebarContext';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';

// BioMedical equipment report. Counts tickets per device so a machine that
// fails repeatedly stands out — the category report would hide it among every
// other device in the same category.
const EquipmentReport = () => {
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [categoryCode, setCategoryCode] = useState('');
  const [branchId, setBranchId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => { setPageTitle('Equipment Reports'); }, [setPageTitle]);

  useEffect(() => {
    categoryService.getAll({ dropdown: true })
      .then((r) => setCategories(Array.isArray(r.data) ? r.data : r.data.data || []))
      .catch(() => setCategories([]));
    branchService.getAll({ noPagination: 'true' })
      .then((r) => setBranches(r.data.data || []))
      .catch(() => setBranches([]));
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryCode) params.category_code = categoryCode;
      if (branchId) params.branch_id = branchId;
      if (startDate || endDate) {
        params.filter = 'custom';
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      const res = await advancedReportService.getEquipmentReport(params);
      setRows(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load equipment report');
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryCode, branchId, startDate, endDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const downloadCsv = () => {
    const headers = ['Code', 'Equipment', 'Category', 'Entity', 'Branch', 'Manufacturer',
                     'Model', 'Serial Number', 'Total', 'Active', 'Resolved', 'Reopened',
                     'Resolution %', 'Avg Hours', 'Last Ticket'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = rows.map((r) => [
      r.equipment_code, r.equipment_name, r.category_name, r.region_name, r.branch_name,
      r.manufacturer, r.model, r.serial_number, r.total_tickets, r.active_tickets,
      r.resolved_tickets, r.reopened_tickets, r.resolution_rate,
      r.avg_resolution_hours, r.last_ticket_at ? formatDateDDMMYYYY(r.last_ticket_at) : ''
    ].map(escape).join(','));

    const blob = new Blob([[headers.map(escape).join(','), ...body].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `equipment-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const mono = { fontFamily: 'monospace', fontSize: '0.8125rem', color: '#374151' };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow noBottomPadding className="h-full flex flex-col">
        <PageHeader
          downloadButton={{ label: 'Export CSV', onClick: downloadCsv, variant: 'secondary' }}
          filters={{
            dropdowns: [
              {
                value: categoryCode,
                placeholder: 'All Categories',
                onChange: (e) => setCategoryCode(e?.target ? e.target.value : e),
                options: [{ value: '', label: 'All Categories' },
                          ...categories.map((c) => ({ value: c.code, label: c.name }))]
              },
              {
                value: branchId,
                placeholder: 'All Branches',
                onChange: (e) => setBranchId(e?.target ? e.target.value : e),
                options: [{ value: '', label: 'All Branches' },
                          ...branches.map((b) => ({ value: String(b.id), label: b.name }))]
              }
            ],
            dateRange: {
              startDate,
              endDate,
              onStartDateChange: (e) => setStartDate(e?.target ? e.target.value : e),
              onEndDateChange: (e) => setEndDate(e?.target ? e.target.value : e)
            }
          }}
        />

        <Table
          columns={[
            { header: 'S.No', accessor: 'serial', width: '70px', render: (_, __, i) => i + 1 },
            { header: 'Code', accessor: 'equipment_code', width: '110px',
              render: (v) => <span style={mono}>{v}</span> },
            { header: 'Equipment', accessor: 'equipment_name', width: '200px' },
            { header: 'Category', accessor: 'category_name', width: '150px', render: (v) => v || '—' },
            { header: 'Entity', accessor: 'region_name', width: '130px', render: (v) => v || '—' },
            { header: 'Branch', accessor: 'branch_name', width: '130px', render: (v) => v || '—' },
            { header: 'Total', accessor: 'total_tickets', width: '80px', align: 'center',
              render: (v) => <span className="tabular-nums font-medium">{v}</span> },
            { header: 'Active', accessor: 'active_tickets', width: '80px', align: 'center',
              render: (v) => v > 0
                ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{v}</span>
                : <span className="text-gray-400">0</span> },
            { header: 'Resolved', accessor: 'resolved_tickets', width: '90px', align: 'center' },
            { header: 'Reopened', accessor: 'reopened_tickets', width: '90px', align: 'center',
              render: (v) => v > 0
                ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">{v}</span>
                : <span className="text-gray-400">0</span> },
            { header: 'Resolution %', accessor: 'resolution_rate', width: '110px', align: 'center',
              render: (v) => <span className="tabular-nums">{v}%</span> },
            { header: 'Avg Hours', accessor: 'avg_resolution_hours', width: '100px', align: 'center',
              render: (v) => v !== null && v !== undefined ? <span className="tabular-nums">{v}</span> : '—' },
            { header: 'Last Ticket', accessor: 'last_ticket_at', width: '120px',
              render: (v) => v ? formatDateDDMMYYYY(v) : '—' }
          ]}
          data={rows}
          emptyMessage="No equipment activity for this period"
          loading={loading}
        />
      </Card>
    </div>
  );
};

export default EquipmentReport;
